import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@chakra-ui/react'
import { PCDAlignmentIcon } from '@editor/assets/icons'
import { USER_TYPES } from '@editor/config/constants'
import { useDocumentTitle } from '@editor/hooks/useDocumentTitle'
import { PointArray } from '@editor/interfaces/attribute'
import { InspectionArea } from '@editor/interfaces/inspection'
import { InspectionAreaSwitcher } from '@editor/pages/projects/common/AreaSwitcher'
import { matchesKey } from '@editor/services/Shortcuts'
import { decideActionPermission } from '@editor/services/Validation'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { difference } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { Matrix4, PerspectiveCamera, Scene, Vector3 } from 'three'

import { Steps, ViewSides } from '../definitions'
import {
  LOCAL_FLAGS,
  reset,
  setCanvasRect,
  setCurrentSide,
  setLeftAnchors,
  setLeftInspectionArea,
  setRightAnchors,
  setRightProgress,
  undoAnchor,
} from '../store'
import { collectConnectedAreas } from '../utils'

export function usePCDAlignment() {
  // URL Params
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionAreas = useSelector((state: RootState) => state.page.inspectionAreas)
  const rightInspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const leftInspectionArea = useSelector((state: RootState) => state.pcdAlignment.leftInspectionArea)
  const canvasSize = useSelector((state: RootState) => state.pcdAlignment.canvasRect)
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)

  const isInvited = useSelector((state: RootState) => state.page.isInvited)
  const isOwner = useSelector((state: RootState) => state.page.isOwner)
  const userType = useSelector((state: RootState) => state.user.userType) as keyof typeof USER_TYPES

  // Refs
  const canvasRef = useRef<HTMLCanvasElement>(null)

  const sceneLeftRef = useRef<Scene>(null!)
  const sceneRightRef = useRef<Scene>(null!)
  const sceneFineTuneRef = useRef<Scene>(null!)
  const sceneMainRef = useRef<Scene>(null!)

  const arcballControlsLeftRef = useRef<ArcballControls | null>(null)
  const arcballControlsRightRef = useRef<ArcballControls | null>(null)
  const arcballControlsFineTuneRef = useRef<ArcballControls | null>(null)
  const arcballControlsMainRef = useRef<ArcballControls | null>(null)

  /**
   * Generate connected areas connections.
   *
   * @param ar The inspection area to generate connections for.
   */
  const generateConnectedAreas = useCallback(
    (ar: InspectionArea) =>
      collectConnectedAreas(
        {
          current: {
            area: ar,
            anchors: [],
            center: new Vector3(),
          },
          matrix: new Matrix4().identity(),
          inverted: false,
        },
        ar,
        inspectionAreas,
      ),
    [inspectionAreas],
  )

  /**
   * Setup area switcher's inspection areas.
   *
   * @param ar The current inspection area.
   * @returns The list of inspection areas with their disabled state and tooltip.
   */
  const makeAreaSwitcherList = useCallback(
    (right: InspectionArea, left: InspectionArea | null): InspectionAreaSwitcher[] => {
      if (!right) return []

      const rightConnected = !!Object.values(right.pcd_alignments || {}).length // deleted connections can result in empty objects.

      // If it's not connected at all, it can connect to anything.
      if (!rightConnected) {
        return inspectionAreas.map((area) => ({
          ...area,
          hidden: area.inspection_area_id === right.inspection_area_id,
          isRight: right.inspection_area_id === area.inspection_area_id,
          isLeft: left?.inspection_area_id === area.inspection_area_id,
        }))
      }

      const connectedAreas = generateConnectedAreas(right)
      const linkedAreas = connectedAreas.map((area) => area.current.area.inspection_area_id)

      // If it's connected, disable all areas within the same link except it's own pair (it'll just be an edit at that point).
      // And of course, can't connect to itself.
      const pairs = Object.keys(right.pcd_alignments || {})
      const diff = difference(linkedAreas, pairs)
      return inspectionAreas.map((area) => {
        if (diff.includes(area.inspection_area_id)) {
          return {
            ...area,
            namePostfix: <PCDAlignmentIcon style={{ marginLeft: '5px' }} />,
            disabled: true,
            hidden: area.inspection_area_id === right.inspection_area_id,
            tooltip: <Box color="orange">このエリアはすでに間接的に位置合わせされています。</Box>,
            tooltipPlacement: 'right',
            isRight: right.inspection_area_id === area.inspection_area_id,
            isLeft: left?.inspection_area_id === area.inspection_area_id,
          }
        }

        return {
          ...area,
          namePostfix: linkedAreas.includes(area.inspection_area_id) ? (
            <PCDAlignmentIcon style={{ marginLeft: '5px' }} />
          ) : undefined,
          disabled: false,
          hidden: area.inspection_area_id === right.inspection_area_id,
          tooltip: undefined,
          tooltipPlacement: undefined,
          isRight: right.inspection_area_id === area.inspection_area_id,
          isLeft: left?.inspection_area_id === area.inspection_area_id,
        }
      })
    },
    [inspectionAreas, generateConnectedAreas],
  )

  /**
   * Setup area switcher's inspection areas.
   * We need to mark certain areas as disabled depending on their pcd alignment connection.
   */
  const areas = useMemo(() => {
    if (!rightInspectionArea) return []
    return makeAreaSwitcherList(rightInspectionArea, leftInspectionArea)
  }, [rightInspectionArea, leftInspectionArea, makeAreaSwitcherList])

  // Page title
  useDocumentTitle(
    `${rightInspectionArea?.inspection_area_name || '3D InfraLoop'}${project ? ` · ${project?.project_name}` : ''}`,
  )

  /**
   * If the left inspection area cannot be selected, reset it.
   * Also need to reset if the same area was selected.
   * This only handles right inspection area switching.
   * Left area switching is handled in the PCDActions component.
   */
  useEffect(() => {
    if (LOCAL_FLAGS.ignoreLeftCheck) {
      LOCAL_FLAGS.ignoreLeftCheck = false
      return
    }

    if (!rightInspectionArea) return

    const ars = makeAreaSwitcherList(rightInspectionArea, leftInspectionArea)
    const left = ars.find((area) => area.inspection_area_id === leftInspectionArea?.inspection_area_id)
    const right = ars.find((area) => area.isRight)
    const rightId = new URL(window.location.href).searchParams.get('area')

    // we need to wait for areas to be updated before left can be checked.
    if (!left?.isLeft || right?.inspection_area_id !== rightId) return

    const alignment = right.pcd_alignments?.[left.inspection_area_id]
    if (left.disabled || left.hidden || (!alignment && currentStep === Steps.FineTuneOrSave)) {
      dispatch(setLeftAnchors([]))
      dispatch(setRightAnchors([]))
      dispatch(setLeftInspectionArea(null))
      return
    }

    if (!alignment) return

    // If all is good, set right area's anchors
    const isRightBase = right.inspection_area_id === alignment.base_area_id
    const rightAnchors = isRightBase ? alignment.base_anchor_positions : alignment.target_anchor_positions
    const leftAnchors = isRightBase ? alignment.target_anchor_positions : alignment.base_anchor_positions
    dispatch(setRightAnchors(rightAnchors as PointArray[]))
    dispatch(setLeftAnchors(leftAnchors as PointArray[]))
  }, [leftInspectionArea, rightInspectionArea, currentStep, makeAreaSwitcherList, dispatch])

  /**
   * Set initial progress to 0 when switching inspection area.
   */
  useEffect(() => {
    if (inspection_area_id === rightInspectionArea?.inspection_area_id) return

    dispatch(setRightProgress(0))
  }, [inspection_area_id, rightInspectionArea, dispatch])

  /**
   * Get canvas size.
   */
  const getCanvasSize = useCallback(() => {
    if (!canvasRef.current)
      return {
        x: 0,
        y: 0,
        width: window.innerWidth,
        height: window.innerHeight,
      }
    return canvasRef.current.getBoundingClientRect()
  }, [canvasRef])

  /**
   * Setup cameras.
   *
   * Initialize cameras independently of fiber as they seem to manage it in a way that doesn't work
   * with the switching of controlled cameras between split view.
   */
  const [cameraRight] = useState(() => {
    const camera = new PerspectiveCamera(90, canvasSize.width / 2 / canvasSize.height, 0.1, 2000)
    camera.position.z = 6
    return camera
  })
  const [cameraLeft] = useState(() => {
    const camera = new PerspectiveCamera(90, canvasSize.width / 2 / canvasSize.height, 0.1, 2000)
    camera.position.z = 6
    return camera
  })
  const [fineTuneCamera] = useState(() => {
    const camera = new PerspectiveCamera(90, canvasSize.width / canvasSize.height, 0.1, 2000)
    camera.position.z = 6
    return camera
  })
  const [mainCamera] = useState(() => {
    const camera = new PerspectiveCamera(90, canvasSize.width / canvasSize.height, 0.1, 2000)
    camera.position.z = 6
    return camera
  })

  const handleResize = useCallback(() => {
    const { width, height, x, y } = getCanvasSize()
    cameraLeft.aspect = width / 2 / height
    cameraLeft.updateProjectionMatrix()

    cameraRight.aspect = width / 2 / height
    cameraRight.updateProjectionMatrix()

    fineTuneCamera.aspect = width / height
    fineTuneCamera.updateProjectionMatrix()

    mainCamera.aspect = width / height
    mainCamera.updateProjectionMatrix()

    dispatch(setCanvasRect({ width, height, x, y }))
  }, [cameraLeft, cameraRight, fineTuneCamera, mainCamera, getCanvasSize, dispatch])

  /**
   * Manage camera projection size.
   */
  useEffect(() => {
    if (!canvasRef.current) return () => null

    const undo = (ev: KeyboardEvent) => {
      if (matchesKey(ev, { key: 'z', ctrl: true }, { fallbackToMeta: true })) {
        dispatch(undoAnchor())
      }
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('keydown', undo)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('keydown', undo)
    }
  }, [canvasRef, handleResize, dispatch])

  /**
   * Reset on component unmounts.
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  /**
   * Detect switching side of the canvas as the mouse moves.
   */
  const detectSwitchingSide = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!canvasRef.current) return

      const rect = canvasRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left

      if (x < canvasSize.width / 2) {
        dispatch(setCurrentSide(ViewSides.Left))
      } else {
        dispatch(setCurrentSide(ViewSides.Right))
      }
    },
    [canvasRef, canvasSize, dispatch],
  )

  /**
   * Mixpanel super properties
   */
  const mixpanelSuperPropsRef = useRef({})
  useEffect(() => {
    mixpanelSuperPropsRef.current = {
      Page: 'PCD Alignment',
      'Project ID': project?.project_id,
      'Inspection Area ID': inspection_area_id,
      'Left Inspection Area ID': leftInspectionArea?.inspection_area_id,
      'Right Inspection Area ID': rightInspectionArea?.inspection_area_id,
    }
    mixpanel.register(mixpanelSuperPropsRef.current)
  }, [project, inspection_area_id, leftInspectionArea, rightInspectionArea])

  // Cleanup super properties on unmount
  useEffect(() => () => Object.keys(mixpanelSuperPropsRef.current).forEach((prop) => mixpanel.unregister(prop)), [])

  return {
    isAllowedToModify: decideActionPermission(isOwner, isInvited).PROJECT_DASHBOARD.MODIFY.includes(userType),

    inspectionAreas: areas,
    leftInspectionArea,
    rightInspectionArea,
    canvasRef,
    handleResize,
    detectSwitchingSide,

    sceneLeftRef,
    sceneRightRef,
    sceneFineTuneRef,
    sceneMainRef,

    arcballControlsLeftRef,
    arcballControlsRightRef,
    arcballControlsFineTuneRef,
    arcballControlsMainRef,

    cameraLeft,
    cameraRight,
    fineTuneCamera,
    mainCamera,
  }
}
